import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { CopyOutlined } from '@ant-design/icons';
import DropdownField from '@components/common/form/DropdownField';
import { DATE_FORMAT_DISPLAY, DATE_FORMAT_VALUE, DEFAULT_FORMAT, DeviceKinds } from '@constants';
import { deviceKinds, TABLET_DEVICE_TYPE_ORDER_QRCODE, tabletOrderQrCodeTypes } from '@constants/masterData';
import useBasicForm from '@hooks/useBasicForm';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, DatePickerField, SelectField, TextField } from '@itz/react-cms-element';
import { copyToClipboard } from '@utils';
import { Card, Col, Row } from 'antd';
import dayjs from 'dayjs';

const QrLiveDeviceForm = (props) => {
    const { formId, actions, dataDetail, onSubmit, setIsChangedFormValues, isEditing } = props;
    const translate = useTranslate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const restaurantId = queryParams.get('restaurantId');
    const subKindValues = translate.formatKeys(tabletOrderQrCodeTypes, [ 'label' ]);
    const kinhValues = translate.formatKeys(deviceKinds, [ 'label' ]);

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const handleSubmit = (values) => {
        if (values.expireDate) {
            values.expireDate = dayjs(values.expireDate).format(DEFAULT_FORMAT);
        }
        const formattedValues = {
            ...values,
            restaurantId: restaurantId,
            deviceKind: DeviceKinds.DEVICE_TYPE_TABLET_RANDOM_QRCODE,
        };

        return mixinFuncs.handleSubmit(formattedValues);
    };

    useEffect(() => {
        dataDetail.expireDate = dataDetail.expireDate && dayjs(dataDetail.expireDate, DATE_FORMAT_VALUE);
        form.setFieldsValue({
            ...dataDetail,
            deviceName: dataDetail.deviceName,
            deviceToken: dataDetail.deviceToken,
            deviceKind: DeviceKinds.DEVICE_TYPE_TABLET_RANDOM_QRCODE,
        });
    }, [dataDetail]);

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            required
                            label={<FormattedMessage defaultMessage="Device Name" />}
                            name="deviceName"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={<FormattedMessage defaultMessage="Device Token" />}
                            name="deviceToken"
                        />
                    </Col>
                    {isEditing && 
                        <>
                            <Col span={12}>
                                <TextField
                                    required
                                    label={<FormattedMessage defaultMessage="Username" />}
                                    name="username"
                                    disabled={isEditing}
                                />
                            </Col>
                            <Col span={12}>
                                <TextField
                                    required
                                    label={<FormattedMessage defaultMessage="Queue name" />}
                                    name="queueName"
                                    disabled={isEditing}
                                />
                            </Col>
                            <Col span={12}>
                                <TextField
                                    required
                                    label={<FormattedMessage defaultMessage="Client ID" />}
                                    name="clientId"
                                    disabled={isEditing}
                                    addonAfter={
                                        <CopyOutlined
                                            style={{
                                                color: '#1677ff',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyToClipboard(dataDetail?.clientId, translate);
                                            }}
                                        />
                                    }
                                />
                            </Col>
                            <Col span={12}>
                                <TextField
                                    required
                                    label={<FormattedMessage defaultMessage="Client secret" />}
                                    name="clientSecret"
                                    disabled={isEditing}
                                    addonAfter={
                                        <CopyOutlined
                                            style={{
                                                color: '#1677ff',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyToClipboard(dataDetail?.clientSecret, translate);
                                            }}
                                        />
                                    }
                                />
                            </Col>
                        </>
                    }
                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Kind" />}
                            name="deviceKind"
                            options={kinhValues}
                            placeholder={<FormattedMessage defaultMessage="Kind" />}
                            disabled
                        />
                    </Col>
                    <Col span={12}>
                        <DatePickerField
                            name="expireDate"
                            label={<FormattedMessage defaultMessage="Expiration date" />}
                            format={DATE_FORMAT_DISPLAY}
                            style={{ width: '100%' }}
                            disabledDate={(current) => current && current.isBefore(dayjs().add(1, 'day'), 'day')}
                        />
                    </Col>
                </Row>
                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default QrLiveDeviceForm;